import { closeIconV2 } from '@/shared/ui/assets';
import { CustomButton } from '@/shared/ui/components';
import { Drawer } from 'antd';
import classNames from 'classnames';
import _ from 'lodash';
import { ReactSVG } from 'react-svg';
import { t } from 'ttag';
import { useDeviceCountDrawer } from './useDeviceCountDrawer';
import type { DeviceCountDrawerProps } from './deviceCountDrawer.types';
import './deviceCountDrawer.scss';

/**
 * Renders the side drawer listing the devices behind an RMA ticket's inbound or outbound device count, as a numbered table of device ids. Adds a box-shadow class to the title bar once the scroll state from useDeviceCountDrawer reports the body has scrolled, and closes through handleClose from both the drawer mask and the close button.
 *
 * @param handleClose - Callback invoked to close the drawer.
 * @param open - Whether the drawer is visible.
 * @param title - Heading text shown in the drawer title bar.
 * @param devices - The devices to list, rendered by deviceId.
 * @returns The device count drawer element.
 */
const DeviceCountDrawer = ({ handleClose, open, title, devices }: DeviceCountDrawerProps) => {
  const { state, handleScroll } = useDeviceCountDrawer();

  const closeButton = (
    <CustomButton
      id="close-btn"
      style="neutral"
      type="tertiary"
      size="small"
      icon={<ReactSVG src={closeIconV2} />}
      onClick={handleClose}
      ariaLabel={t`Close`}
    />
  );

  return (
    <Drawer
      id="device-count-drawer"
      aria-label={typeof title === 'string' ? title : undefined}
      rootClassName="custom-drawer device-count-drawer"
      width={500}
      closable={false}
      onClose={handleClose}
      open={open}
    >
      <div
        className={classNames('drawer-title-container', state.isScroll ? '--has-box-shadow' : '')}
      >
        <span className="drawer-title">{title}</span>
        {closeButton}
      </div>
      <div id="drawer-body-container" className="drawer-body-container" onScroll={handleScroll}>
        <table aria-label={t`Device list`}>
          <thead>
            <tr>
              <th>#</th>
              <th>{t`Device ID`}</th>
            </tr>
          </thead>
          <tbody>
            {_.map(devices, (device, index) => (
              <tr>
                <td>{index + 1}</td>
                <td className="custom-row">{device.deviceId}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Drawer>
  );
};

export default DeviceCountDrawer;
